import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import {
  Plus, PencilSimple, Trash, ArrowUp, ArrowDown,
  FacebookLogo, YoutubeLogo, InstagramLogo, TwitterLogo, LinkedinLogo, TiktokLogo,
  WhatsappLogo, TelegramLogo, MessengerLogo, Phone, Envelope, Globe, Link as LinkIcon
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable icon tokens → Phosphor components
export const SOCIAL_ICON_MAP = {
  facebook: FacebookLogo,
  youtube: YoutubeLogo,
  instagram: InstagramLogo,
  twitter: TwitterLogo,
  linkedin: LinkedinLogo,
  tiktok: TiktokLogo,
  whatsapp: WhatsappLogo,
  telegram: TelegramLogo,
  messenger: MessengerLogo,
  phone: Phone,
  envelope: Envelope,
  globe: Globe,
  link: LinkIcon,
};

const ICON_OPTIONS = Object.keys(SOCIAL_ICON_MAP);

const TYPE_OPTIONS = [
  { value: 'link', label: 'Link (URL)' },
  { value: 'phone', label: 'Phone (auto WhatsApp)' },
  { value: 'email', label: 'Email (auto mailto)' },
];

const PLACEHOLDER_BY_TYPE = {
  link: 'https://example.com',
  phone: '+261340000000',
  email: 'support@ato.mg',
};

const emptyItem = () => ({ name: '', type: 'link', value: '', icon: 'link', is_active: true });

export const SocialLinksManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/social-links`, { withCredentials: true });
      setItems(data || []);
    } catch {
      toast.error('Failed to load social links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => setDialog({ open: true, mode: 'create', data: emptyItem() });
  const openEdit = (item) => setDialog({ open: true, mode: 'edit', data: { ...item } });

  const save = async () => {
    const { mode, data } = dialog;
    if (!data.name.trim()) { toast.error('Name is required'); return; }
    if (!data.value.trim()) { toast.error('Value is required'); return; }
    try {
      const payload = {
        name: data.name.trim(),
        type: data.type,
        value: data.value.trim(),
        icon: data.icon,
        is_active: !!data.is_active,
      };
      if (mode === 'create') {
        await axios.post(`${API}/admin/social-links`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/social-links/${data.item_id}`, payload, { withCredentials: true });
      }
      setDialog({ open: false, mode: 'create', data: null });
      fetchItems();
      toast.success(mode === 'create' ? 'Item added' : 'Item updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/social-links/${id}`, { withCredentials: true });
      fetchItems();
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API}/admin/social-links/${item.item_id}`,
        { is_active: !item.is_active }, { withCredentials: true });
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await axios.put(`${API}/admin/social-links/reorder`,
        { item_ids: reordered.map(i => i.item_id) }, { withCredentials: true });
      fetchItems();
    } catch { toast.error('Reorder failed'); }
  };

  if (loading) {
    return <div className="text-center py-12"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="social-links-management">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-text_main">Social Media & Contact</h3>
          <p className="text-sm text-text_muted mt-0.5">Manage social links, phone and email contacts displayed on the site.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-social-button">
          <Plus size={16} weight="bold" className="mr-1" /> Add Contact / Social
        </Button>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-text_muted text-center py-8">No items yet.</p>
      )}

      <div className="space-y-2">
        {items.map((item, idx) => {
          const Icon = SOCIAL_ICON_MAP[item.icon] || LinkIcon;
          return (
            <div key={item.item_id}
              className={`flex items-center gap-3 border border-border rounded-md bg-white px-3 py-2.5 ${item.is_active ? '' : 'opacity-60'}`}
              data-testid={`social-item-${item.item_id}`}
            >
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30">
                  <ArrowUp size={14} weight="bold" />
                </button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="disabled:opacity-30">
                  <ArrowDown size={14} weight="bold" />
                </button>
              </div>
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon size={18} weight="regular" className="text-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-text_main text-sm truncate">{item.name}</p>
                  <span className="text-[10px] font-semibold text-text_muted bg-surface_muted px-1.5 py-0.5 rounded">{item.type}</span>
                </div>
                <p className="text-xs text-text_muted truncate">
                  {item.value}
                  {item.type !== 'link' && item.href && (
                    <span className="ml-2 text-text_muted">→ {item.href}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch checked={item.is_active} onCheckedChange={() => toggleActive(item)} data-testid={`social-active-${item.item_id}`} />
                <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-social-${item.item_id}`}>
                  <PencilSimple size={14} />
                </Button>
                <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirmDelete(item)} data-testid={`delete-social-${item.item_id}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialog.open} onOpenChange={(v) => !v && setDialog({ open: false, mode: 'create', data: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog.mode === 'create' ? 'Add Social / Contact' : 'Edit Social / Contact'}</DialogTitle>
          </DialogHeader>
          {dialog.data && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold">Name</Label>
                <Input value={dialog.data.name} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, name: e.target.value } })} placeholder="e.g. Instagram, Support Email" data-testid="social-name-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Type</Label>
                <Select value={dialog.data.type} onValueChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, type: v } })}>
                  <SelectTrigger data-testid="social-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold">Value</Label>
                <Input value={dialog.data.value} onChange={(e) => setDialog({ ...dialog, data: { ...dialog.data, value: e.target.value } })} placeholder={PLACEHOLDER_BY_TYPE[dialog.data.type]} data-testid="social-value-input" />
                <p className="text-[11px] text-text_muted mt-1">
                  {dialog.data.type === 'phone' && 'Auto-generates a WhatsApp link (wa.me).'}
                  {dialog.data.type === 'email' && 'Auto-generates a mailto link.'}
                  {dialog.data.type === 'link' && 'Must start with http:// or https://'}
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold">Icon</Label>
                <div className="grid grid-cols-6 gap-2 mt-1">
                  {ICON_OPTIONS.map(key => {
                    const Icon = SOCIAL_ICON_MAP[key];
                    const selected = dialog.data.icon === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setDialog({ ...dialog, data: { ...dialog.data, icon: key } })}
                        className={`w-10 h-10 flex items-center justify-center rounded-md border ${selected ? 'border-primary bg-primary/10' : 'border-border bg-white hover:border-primary/40'}`}
                        title={key}
                        data-testid={`icon-option-${key}`}
                      >
                        <Icon size={18} weight="regular" className={selected ? 'text-primary' : 'text-text_muted'} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Active (visible on frontend)</Label>
                <Switch checked={dialog.data.is_active} onCheckedChange={(v) => setDialog({ ...dialog, data: { ...dialog.data, is_active: v } })} data-testid="social-active-toggle" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, mode: 'create', data: null })}>Cancel</Button>
            <Button onClick={save} data-testid="save-social-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{confirmDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-danger hover:bg-danger" onClick={() => { remove(confirmDelete.item_id); setConfirmDelete(null); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
